import {createRouter, createWebHistory, createWebHashHistory} from 'vue-router';
import { setAuditScreen } from '@/utils/clientHeaders';
import { clearSupport, getAccessToken, getSupportSession, isSupportEnded, isSupportTab } from '@/utils/tokenStore';
import { SUPPORT_ENDED_PATH, SUPPORT_ENTER_PATH, supportEndedUrl } from '@/utils/supportSession';
const SupportEnter = () => import(/* webpackChunkName: "Support" */ '../views/SupportEnter.vue');
const SupportEnded = () => import(/* webpackChunkName: "Support" */ '../views/SupportEnded.vue');
const AuditLogList = () => import(/* webpackChunkName: "AuditLog" */ '../views/AuditLog/AuditLogList.vue');
const HandoffEnter = () => import(/* webpackChunkName: "Support" */ '../views/HandoffEnter.vue');
import { ENTER_PATH } from '@/utils/handoff';
import { isLegacyHostApp } from '@/utils/deviceCheck';
import NoticeList from '../views/NoticeList.vue';
import NoticeRead from '../views/NoticeRead.vue';
import Signin from '../views/Signin/Signin.vue';
import ResetPwd from '../views/Signin/ResetPwd.vue';
import ProductsList from '../views/Products/ProductsList.vue';
import CertificateList from '../views/CertificateList.vue';
import ItemsList from '../views/items/ItemsList.vue';
import ApiKeyList from '../views/ApiKeyList.vue';
import HosManage from '../views/HospitalManagement/HosManage.vue';
import AddHospital from '../views/HospitalManagement/AddHospital.vue';
import ModifyHospital from '../views/HospitalManagement/ModifyHospital.vue';
import ReviewManagement from '../views/Review/ReviewManagement.vue';
import IssueCertificate from '../views/CertificateSettings/IssueCertificate.vue';
import CompanyInfo from '../views/CompanyInfo.vue';
import AddItem from '../views/items/AddItem.vue';
import ModifyItem from '../views/items/ModifyItem.vue';
import Dashboard from '../views/Dashboard.vue';
import ManuAccManage from '../views/ManuAccManage/ManuAccManage.vue';
// SPEC: 전자계약 v1->v2 전환 1차 화면. contracts-api를 호출하는 신규 목록 화면 (v1 ContractList는 무변경)
import ContractListV2 from '../views/ContractManagementV2/ContractListV2.vue';
import ContractTypeListV2 from '../views/ContractManagementV2/ContractTypeListV2.vue';
// SPEC: 전자계약 v1->v2 전환 3차 화면. 물품매매V4(MulpumV6) v2 계약 생성 화면 — contracts-api 호출
import MulpumV6V2 from '@/views/ContractManagementV2/contracts/dentium/MulpumV6V2.vue';
// 2026-08-15: v2 창구 나머지 4종 생성 화면 (contracts-api 매퍼·프릭스 템플릿 준비 완료분)
import BnkV2 from '@/views/ContractManagementV2/contracts/dentium/BnkV2.vue';
import SinhyupV2 from '@/views/ContractManagementV2/contracts/dentium/SinhyupV2.vue';
import SeolqiChairV2 from '@/views/ContractManagementV2/contracts/dentium/SeolqiChairV2.vue';
import SeolqiCbctV2 from '@/views/ContractManagementV2/contracts/dentium/SeolqiCbctV2.vue';
import ActilinkRentalV2 from '@/views/ContractManagementV2/contracts/plasmapp/ActilinkRentalV2.vue';
import ActilinkRentalV3 from '../views/ContractManagementV3/contracts/plasmapp/ActilinkRentalV3.vue';
import {resolveContractCreateRouteName} from '@/views/ContractManagementV2/routeMap';
// 2026-08-24: v3 창구 — QA 가 v2 를 검증하는 동안 개발을 이어가기 위한 별도 트리.
// 화면은 v2 복사본이고, 차이는 (1) 라우트 경로/이름이 전부 v3, (2) 발행 요청에
// serviceProvider=PRIX 를 실어 보내는 것(ContractManagementV3/api.js) 둘뿐이다.
// ACTILINK 는 v3 대상에서 제외했다(routeMap 주석 참조).
import ContractListV3 from '../views/ContractManagementV3/ContractListV3.vue';
import ContractTypeListV3 from '../views/ContractManagementV3/ContractTypeListV3.vue';
import MulpumV6V3 from '@/views/ContractManagementV3/contracts/dentium/MulpumV6V3.vue';
import BnkV3 from '@/views/ContractManagementV3/contracts/dentium/BnkV3.vue';
import SinhyupV3 from '@/views/ContractManagementV3/contracts/dentium/SinhyupV3.vue';
import SeolqiChairV3 from '@/views/ContractManagementV3/contracts/dentium/SeolqiChairV3.vue';
import SeolqiCbctV3 from '@/views/ContractManagementV3/contracts/dentium/SeolqiCbctV3.vue';
// v2 와 동명 함수라 별칭으로 받는다 — 둘을 섞으면 v3 카드가 v2 라우트로 가 버린다.
import {resolveContractCreateRouteName as resolveV3CreateRouteName} from '@/views/ContractManagementV3/routeMap';

// 계약관리 V5 — 물품매매 개정 서식 전용. v3 와 분리된 별도 트리이며 물품매매 화면만 실체가 있다.
import ContractListV5 from '@/views/ContractManagementV5/ContractListV5.vue';
import ContractTypeListV5 from '@/views/ContractManagementV5/ContractTypeListV5.vue';
import MulpumV5V5 from '@/views/ContractManagementV5/contracts/dentium/MulpumV5V5.vue';
import {MULPUM_V5_ROUTE_NAME} from '@/views/ContractManagementV5/contracts/dentium/MulpumV5V5.vue';
// 물품매매V6(2차 개정 서식) — V5 와 공존하는 계약유형. 2026-10-29 부터 유형 카드는 V6 로 간다(routeMap 값),
// V5 는 직접 주소(/v5/contract/mulpum-v5/:templateId)로만 닿는다(임시저장 복원·비상용).
// V5·V6 라우트 name 은 둘 다 routeMap 을 거치지 않고 화면 쪽 고정 상수를 그대로 가져온다 — 두 이름이
// 겹치면 vue-router 가 먼저 등록된 V5 라우트를 지우므로(scripts/contracts-v6-preserve-check.js (f)) 떼어 둔다.
import MulpumTypeV6 from '@/views/ContractManagementV5/contracts/dentium/MulpumTypeV6.vue';
import {MULPUM_V6_ROUTE_NAME} from '@/views/ContractManagementV5/contracts/dentium/MulpumTypeV6.vue';
// 전자계약 고정 주소(버전 없음). 아래 v5 목록·유형 라우트에 alias 로 붙는다 —
// 창구를 옮길 때 이 두 alias 만 새 트리로 옮기면 메뉴 DB·서버·화면은 그대로다.
// 근거와 규칙은 utils/contractConsole.js 주석 참조.
import {CONTRACT_LIST_PATH, CONTRACT_TYPES_PATH} from '@/utils/contractConsole';
import GoalManagement from "@/views/ContractManagement/dashboard/GoalManagement.vue";
import GoalManagementV2 from "@/views/ContractManagement/dashboard/GoalManagementV2.vue";
import DentiumErpGoalManagement from "@/views/SalesRecordManagement/dashboard/DentiumErpGoalManagement.vue";
import SubscriptionList from "@/views/SubscriptionManagement/plasmapp/SubscriptionList.vue";
import SalesRecordList from "@/views/SalesRecordManagement/salesrecords/SalesRecordList.vue";
import EventList from "@/views/EventManagement/EventList.vue";
import EventDashboard from "@/views/EventManagement/EventDashboard.vue";
import AddEvent from "@/views/EventManagement/AddEvent.vue";
import EventListV2 from "@/views/EventManagementV2/EventListV2.vue";
import EventStatistics from "@/views/EventManagementV2/EventStatistics.vue";
import ReviewEventSummary from "@/views/EventManagementV2/ReviewEventSummary.vue";
import DailyStatistics from "@/views/EventManagementV2/DailyStatistics.vue";
import AttendeeSegmentRaw from "@/views/EventManagementV2/AttendeeSegmentRaw.vue";
import EventReport from "@/views/EventManagementV2/report/EventReport.vue";
import CustomerDetails from "@/views/CustomerDetails.vue";
import SwiftList from "@/views/Swift/SwiftList.vue";
import IssueSwift from "@/views/Swift/IssueSwift.vue";
import ModifySwift from "@/views/Swift/ModifySwift.vue";
import CenterflowSubscriptionList from '@/views/SubscriptionManagement/centerflow/SubscriptionList.vue';
import BrandSettings from '@/views/BrandManage/BrandSettings.vue';
import CertificateWorkOrder from '@/views/CertificateWorkOrder/index.vue';
import CertificateWorkOrderList from '@/views/CertificateWorkOrderList/index.vue';
import ProductCertificateLink from '@/views/ProductCertificateLink.vue';
import GtinManagementList from '@/views/GtinManagement/GtinManagementList.vue';
import BrightmallOrders from '@/views/brightmall/BrightmallOrders.vue';
// SPEC-CONTRACT-VIEW-GRANT-001: 전자계약 조회권한 관리(덴티움 마스터 전용)
import ContractViewPolicy from '@/views/ContractViewPolicy/ContractViewPolicy.vue';

const PromotionList = () =>
  import(/* webpackChunkName: "PromotionList" */ "../views/PromotionManagement/PromotionList.vue");
const PromotionCreate = () =>
  import(/* webpackChunkName: "PromotionCreate" */ "../views/PromotionManagement/PromotionCreate.vue");
const PromotionDetail = () =>
  import(/* webpackChunkName: "PromotionDetail" */ "../views/PromotionManagement/PromotionDetail.vue");
const PromotionEntries = () =>
  import(/* webpackChunkName: "PromotionEntries" */ "../views/PromotionManagement/PromotionEntries.vue");
const PromotionEntryDetail = () =>
  import(/* webpackChunkName: "PromotionEntryDetail" */ "../views/PromotionManagement/PromotionEntryDetail.vue");

const ShareLinksPage = () =>
  import(/* webpackChunkName: "ShareLinksPage" */ "../views/ShareLinks/ShareLinksPage.vue");
const ShareLinkStatsPage = () =>
  import(/* webpackChunkName: "ShareLinkStatsPage" */ "../views/ShareLinks/ShareLinkStatsPage.vue");

const AlimtalkChannelSettings = () =>
  import(/* webpackChunkName: "AlimtalkChannelSettings" */ "../views/Alimtalk/ChannelSettings.vue");
const AlimtalkTemplateList = () =>
  import(/* webpackChunkName: "AlimtalkTemplateList" */ "../views/Alimtalk/TemplateList.vue");
const AlimtalkTemplateCreate = () =>
  import(/* webpackChunkName: "AlimtalkTemplateCreate" */ "../views/Alimtalk/TemplateCreate.vue");
const AlimtalkTemplateEdit = () =>
  import(/* webpackChunkName: "AlimtalkTemplateEdit" */ "../views/Alimtalk/TemplateEdit.vue");
const AlimtalkTemplateBulkCreate = () =>
  import(/* webpackChunkName: "AlimtalkTemplateBulkCreate" */ "../views/Alimtalk/TemplateBulkCreate.vue");

import {RouterEnum} from "@/enums/router.enums";
import {jwtDecoder} from "@/utils/utils";
import {isBlockedOnMobile} from "@/utils/mobileAccess";
import store from "@/store";
// 권한 없는 메뉴 주소 → 첫 화면 + 안내(11-01). 보존 검사(삭제 0줄) 때문에 위 import 줄을 고치지 않고 따로 더함
import { getProfile } from '@/utils/tokenStore';
import { menuKeyOfLink, routeDenial, MENU_DENIED_TEXT } from '@/utils/permUi';
// 지원 접속에서만 여는 화면(11-02). 보존 검사(삭제 0줄) 때문에 위 줄을 고치지 않고 따로 더함
import { isOperatorAdminToken, isSupportOnlyPath } from '@/utils/permUi';
import { permissionDeniedText } from '@/utils/standardErrors';
import { message } from 'ant-design-vue';

const MobileBlocked = () =>
  import(/* webpackChunkName: "MobileBlocked" */ "../views/MobileBlocked.vue");
const MyPage = () =>
  import(/* webpackChunkName: "MyPage" */ "../views/MyPage.vue");
const MobileLanding = () =>
  import(/* webpackChunkName: "MobileLanding" */ "../views/MobileLanding.vue");
const ProductQrPage = () =>
  import(/* webpackChunkName: "ProductQrPage" */ "../views/ProductQr/ProductQrPage.vue");
const ProductDevicePage = () =>
  import(/* webpackChunkName: "ProductDevicePage" */ "../views/ProductDevice/ProductDevicePage.vue");
import { CODE_MANAGEMENT, HANDPIECE_BARCODE } from "../views/CodeManagement/codeManagementRoutes";
const HandpieceBarcodePage = () =>
  import(/* webpackChunkName: "HandpieceBarcodePage" */ "../views/CodeManagement/HandpieceBarcodePage.vue");
import { GENUINE_TIP_CODE } from "../views/CodeManagement/codeManagementRoutes";
const GenuineTipCodePage = () =>
  import(/* webpackChunkName: "GenuineTipCodePage" */ "../views/CodeManagement/GenuineTipCodePage.vue");
const ProductLabelList = () =>
  import(/* webpackChunkName: "ProductLabel" */ "../views/ProductLabel/ProductLabelList.vue");
const ProductLabelRegister = () =>
  import(/* webpackChunkName: "ProductLabel" */ "../views/ProductLabel/ProductLabelRegister.vue");
const ProductLabelDetail = () =>
  import(/* webpackChunkName: "ProductLabel" */ "../views/ProductLabel/ProductLabelDetail.vue");
const ProductLabelEdit = () =>
  import(/* webpackChunkName: "ProductLabel" */ "../views/ProductLabel/ProductLabelEdit.vue");
const ProductLabelQrReassign = () =>
  import(/* webpackChunkName: "ProductLabel" */ "../views/ProductLabel/ProductLabelQrReassign.vue");
const ProductLabelPending = () =>
  import(/* webpackChunkName: "ProductLabel" */ "../views/ProductLabel/ProductLabelPending.vue");
const ProductLabelHistory = () =>
  import(/* webpackChunkName: "ProductLabel" */ "../views/ProductLabel/ProductLabelHistory.vue");

const companyId = jwtDecoder(localStorage.getItem('accessTokenManu'));

/**
 * 모바일 여부. 부를 때마다 다시 잰다.
 *
 * 예전에는 `const isMobile = window.innerWidth < 768` 로 모듈이 처음 읽힐 때 한 번만 계산했다.
 * 그래서 화면을 돌리거나 창 크기를 바꿔도 값이 그대로였고, 좁은 창에서 로그인한 뒤 넓혀도
 * 계속 모바일로 판정됐다. ContractList.vue 가 이미 windowWidth 를 다시 재는 방식을 쓰고 있어 그것과 맞춘다.
 */
const isMobile = () => window.innerWidth < 768;

/**
 * 구버전 앱의 도착 화면 — 전자계약 목록.
 *
 * 001 이전 운영에서 폰이 가던 곳이다(`isMobile ? '/contract/list' : Dashboard`).
 * 구버전 앱의 네이티브 첫 탭 이름이 '전자계약'이라 이 화면이어야 이름과 맞는다.
 *
 * 2026-08-28: v1 목록(/contract/list) 제거에 따라 v3 목록으로 옮겼다.
 * 운영 menus 의 '전자 계약' 링크도 /v3/contract/list 라 착지 화면이 메뉴와 일치한다.
 *
 * 스토어의 구버전이 충분히 줄면 이 상수와 isLegacyHostApp 분기를 통째로 지운다.
 */
// 2026-09-23: 창구 버전이 박힌 주소를 고정 주소로 바꿨다. 창구를 옮겨도 이 줄은 그대로다.
const LEGACY_APP_LANDING = CONTRACT_LIST_PATH;

const routes = [
    // 로그인
    {
        path: RouterEnum.SignIn.path,
        name: RouterEnum.SignIn.name,
        component: Signin,
    },
    {
        path: RouterEnum.ResetPwd.path,
        name: RouterEnum.ResetPwd.name,
        component: ResetPwd,
    },
    {
        path: '/',
        name: '/',
        /*
         * PC 는 대시보드로. 사이드바로 어디든 갈 수 있으니 자연스러운 첫 화면이다.
         *
         * 폰은 첫 탭으로 가야 한다. 하단 탭이 유일한 이동 수단이라 탭에 없는 화면에
         * 떨어지면 안 되는데, 앱이 여는 주소가 정확히 '/' 다(EndPoint.webViewUrl).
         * 대시보드를 탭에서 뺀 제조사(덴티움)는 앱을 켤 때마다 그 상황이 됐다.
         *
         * 첫 탭은 서버 응답 두 개가 있어야 정해지는데 여기서는 기다릴 수 없다
         * (redirect 는 동기다). 그래서 경유 화면으로 넘기고 거기서 받아 온다.
         *
         * 가드에서 처리하지 않는 이유: vue-router 4 는 라우트 레코드의 redirect 를
         * 매칭 단계에서 먼저 풀기 때문에, beforeEach 가 받는 to.path 는 이미
         * 리다이렉트된 값이다. '/' 를 조건으로 잡을 수 없다.
         *
         * 기계설비건설공제조합은 아래 가드가 /swift/list 로 다시 보낸다.
         *
         * 구버전 앱은 전자계약 목록으로 보낸다 — 001 이전의 동작이다.
         *
         * 구버전 앱의 네이티브 첫 탭 이름이 '전자계약'이고, 그 탭이 여는 주소가 '/' 다.
         * 001 이전에는 여기가 `isMobile ? '/contract/list' : Dashboard` 라서
         * 탭 이름과 화면이 맞았다. 대시보드로 보내면 전자계약 탭에 대시보드가 뜬다.
         *
         * 구버전 앱에는 웹 하단 탭을 그리지 않으므로(App.vue showMobileChrome)
         * 이동 수단이 네이티브 탭 둘뿐이다. 그래서 도착지가 특히 중요하다.
         *
         * 판정에 store 가 아니라 UA 를 쓰는 이유(isLegacyHostApp 주석 참고):
         * 이 redirect 는 콜드 스타트에 딱 한 번, 앱이 버전을 알려주기 전에 실행된다.
         */
        redirect: () => {
            // 폭을 먼저 본다 — 001 이전이 `isMobile ? '/contract/list' : Dashboard` 라
            // 넓은 화면(아이패드 가로 등)에서는 구버전 앱이라도 대시보드였다.
            if (!isMobile()) return RouterEnum.Dashboard.path;
            return isLegacyHostApp() ? LEGACY_APP_LANDING : RouterEnum.MobileLanding.path;
        },
    },
    { // 폰에서 '/' 로 들어왔을 때 첫 탭을 정해 주는 경유 화면
        path: RouterEnum.MobileLanding.path,
        name: RouterEnum.MobileLanding.name,
        component: MobileLanding,
    },
    { // 대쉬보드
        path: RouterEnum.Dashboard.path,
        name: RouterEnum.Dashboard.name,
        component: Dashboard,
    },
    { // 품목관리
        path: RouterEnum.ItemManagement.path,
        name: RouterEnum.ItemManagement.name,
        redirect: `${RouterEnum.ItemManagement.path}/${RouterEnum.ItemManagement.ItemList.path}`,
        children: [
            {
                path: RouterEnum.ItemManagement.ItemList.path,
                name: RouterEnum.ItemManagement.ItemList.name,
                component: ItemsList,
            },
            {
                path: RouterEnum.ItemManagement.AddItem.path,
                name: RouterEnum.ItemManagement.AddItem.name,
                component: AddItem,
            },
            {
                path: RouterEnum.ItemManagement.ModifyItem.path,
                name: RouterEnum.ItemManagement.ModifyItem.name,
                component: ModifyItem,
            },
        ]
    },
    { // 제품관리
        path: RouterEnum.ProductManagement.path,
        name: RouterEnum.ProductManagement.name,
        children: [
            { // 대량업로드
                path: '',
                name: RouterEnum.ProductManagement.name,
                component: ProductsList,
            },
            // { // 대량업로드
            //   path: 'csv-upload',
            //   name: 'CsvUpload',
            //   component: CsvUpload,
            // },
        ]
    },
    { // 발행 내역 조회
        path: RouterEnum.Certificate.path,
        name: RouterEnum.Certificate.name,
        redirect: `${RouterEnum.Certificate.path}/${RouterEnum.Certificate.CertificateList.path}`,
        children: [
            { // 발행 내역
                path: RouterEnum.Certificate.CertificateList.path,
                name: RouterEnum.Certificate.CertificateList.name,
                component: CertificateList
            },
            {
                path: RouterEnum.Certificate.IssueCertificate.path,
                name: RouterEnum.Certificate.IssueCertificate.name,
                component: IssueCertificate,
            },
        ]
    },
    {
        path: RouterEnum.HospitalManagement.path,
        name: RouterEnum.HospitalManagement.name,
        redirect: `${RouterEnum.HospitalManagement.path}/${RouterEnum.HospitalManagement.HospitalList.path}`,
        children: [
            { // 병원 관리
                path: RouterEnum.HospitalManagement.HospitalList.path,
                name: RouterEnum.HospitalManagement.HospitalList.name,
                component: HosManage,
            },
            // { // 병원 계정관리
            //   path: RouterEnum.HospitalManagement.HospitalAccountManagement.path,
            //   name: RouterEnum.HospitalManagement.HospitalAccountManagement.name,
            //   component: HosAccManage,
            // },
            { // 병원 등록
                path: RouterEnum.HospitalManagement.AddHospital.path,
                name: RouterEnum.HospitalManagement.AddHospital.name,
                component: AddHospital,
            },
            { // 병원 수정
                path: RouterEnum.HospitalManagement.ModifyHospital.path,
                name: RouterEnum.HospitalManagement.ModifyHospital.name,
                component: ModifyHospital,
            },
            { // 후기 관리
                path: RouterEnum.HospitalManagement.ReviewManagement.path,
                name: RouterEnum.HospitalManagement.ReviewManagement.name,
                component: ReviewManagement,
            },
        ]
    },
    { // 계약관리 — v1 발행/목록 화면은 2026-08-28 제거(전자계약 발행을 프릭스 v3 로 일원화).
      // 남는 것은 '목표 실적 관리' 하나뿐이다. 운영 menus 의 해당 메뉴 링크가 /contract/goal 이라
      // 경로를 유지해야 하고, 계약서 화면이 아니라 실적 대시보드라 v3 이관 대상도 아니다.
        path: RouterEnum.ContractManagement.path,
        name: RouterEnum.ContractManagement.name,
        redirect: `${RouterEnum.ContractManagement.path}/${RouterEnum.ContractManagement.Goal.path}`,
        children: [
            {
                path: RouterEnum.ContractManagement.Goal.path,
                name: RouterEnum.ContractManagement.Goal.name,
                component: GoalManagement,
            },
            {
                path: RouterEnum.ContractManagement.GoalV2.path,
                name: RouterEnum.ContractManagement.GoalV2.name,
                component: GoalManagementV2,
            },
        ]
    },
    { // 계약관리 V2 (contracts-api 전환용 신규 목록 화면. v1 계약관리 라우트와 완전히 분리된 별도 트리)
        path: '/v2/contract',
        name: 'ContractManagementV2',
        redirect: '/v2/contract/list',
        children: [
            {
                path: 'list',
                name: 'ContractListV2',
                component: ContractListV2,
            },
            {
                // v1 name('ContractTypeList')과 충돌 방지 — v1/v2 화면이 라우터 name 공간을 공유하므로
                // 반드시 별도 이름을 쓴다.
                path: 'types',
                name: 'ContractTypeListV2',
                component: ContractTypeListV2,
            },
            {
                // 물품매매V4(MulpumV6) v2 생성 화면. path는 v1(sales-contract-v6/:templateId)을
                // 그대로 미러링하되 /v2/contract 트리 아래로 옮겼다. name은 routeMap.js의 매핑
                // (v1 name + 'V2')과 반드시 같은 값이어야 한다 — ContractItemV2가 이 name으로
                // $router.push하므로, 둘이 어긋나면 카드 클릭이 조용히 실패한다.
                path: `sales-contract-v6/:templateId`,
                name: resolveContractCreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.MulpumV6.name),
                component: MulpumV6V2,
                props: true,
            },
            // v2 창구 나머지 4종 — path 는 v1 을 미러링해 /v2/contract 트리 아래로. name 은
            // routeMap.js 매핑(v1 name + 'V2')과 반드시 같은 값(ContractItemV2 push 대상).
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.BNK.path}/:templateId`,
                name: resolveContractCreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.BNK.name),
                component: BnkV2,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.SINHYUP.path}/:templateId`,
                name: resolveContractCreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.SINHYUP.name),
                component: SinhyupV2,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.SeolqiChair.path}/:templateId`,
                name: resolveContractCreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.SeolqiChair.name),
                component: SeolqiChairV2,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.SeolqiCbct.path}/:templateId`,
                name: resolveContractCreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.SeolqiCbct.name),
                component: SeolqiCbctV2,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Plasmapp.ActilinkRental.path}/:templateId`,
                name: resolveContractCreateRouteName(RouterEnum.ContractManagement.Contracts.Plasmapp.ActilinkRental.name),
                component: ActilinkRentalV2,
                props: true,
            },
        ]
    },
    { // 계약관리 V3 (QA 가 v2 를 검증하는 동안 개발을 잇는 창구. v1/v2 와 완전히 분리된 별도 트리)
        path: '/v3/contract',
        name: 'ContractManagementV3',
        redirect: '/v3/contract/list',
        children: [
            {
                path: 'list',
                name: 'ContractListV3',
                component: ContractListV3,
            },
            {
                // v1('ContractTypeList')·v2('ContractTypeListV2') 와 name 공간을 공유하므로 별도 이름.
                path: 'types',
                name: 'ContractTypeListV3',
                component: ContractTypeListV3,
            },
            {
                // path 는 v1/v2 를 미러링해 /v3/contract 트리 아래로. name 은 v3 routeMap 매핑
                // (v1 name + 'V3')과 반드시 같은 값이어야 한다 — ContractItemV3 가 이 name 으로
                // push 하므로, 둘이 어긋나면 카드 클릭이 조용히 실패한다.
                path: `sales-contract-v6/:templateId`,
                name: resolveV3CreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.MulpumV6.name),
                component: MulpumV6V3,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.BNK.path}/:templateId`,
                name: resolveV3CreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.BNK.name),
                component: BnkV3,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.SINHYUP.path}/:templateId`,
                name: resolveV3CreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.SINHYUP.name),
                component: SinhyupV3,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.SeolqiChair.path}/:templateId`,
                name: resolveV3CreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.SeolqiChair.name),
                component: SeolqiChairV3,
                props: true,
            },
            {
                path: `${RouterEnum.ContractManagement.Contracts.Dentium.SeolqiCbct.path}/:templateId`,
                name: resolveV3CreateRouteName(RouterEnum.ContractManagement.Contracts.Dentium.SeolqiCbct.name),
                component: SeolqiCbctV3,
                props: true,
            },
            {
                // 2026-08-28: ACTILINK 를 v3 에 추가(사용자 결정, 08-24 제외 결정 철회).
                // v1 창구 제거가 확정되면서 기존의 'v1 create 폴백'이 성립하지 않게 됐다.
                // 발행 경로는 v2 와 동일하게 모두싸인 유지 — 플라즈맵은 프릭스 전환 범위 밖이다.
                path: `${RouterEnum.ContractManagement.Contracts.Plasmapp.ActilinkRental.path}/:templateId`,
                name: resolveV3CreateRouteName(RouterEnum.ContractManagement.Contracts.Plasmapp.ActilinkRental.name),
                component: ActilinkRentalV3,
                props: true,
            },
        ]
    },
    { // 계약관리 V5 — 물품매매 개정 서식(계약유형 물품매매V5) 전용 창구.
      //
      // v3 와 **완전히 분리된 별도 트리**다. v3 파일은 한 줄도 건드리지 않는다 —
      // 검증이 끝날 때까지 운영은 v3 로 계속 돌아야 하므로 공존이 전제다.
      //
      // 실체가 있는 create 화면은 물품매매 하나뿐이다. 나머지 5종은 서식이 바뀌지 않아
      // routeMap 이 v3 라우트로 보낸다(ContractManagementV5/routeMap.js 주석 참조).
        path: '/v5/contract',
        name: 'ContractManagementV5',
        redirect: '/v5/contract/list',
        children: [
            {
                // [창구 교체 지점 1/2] 고정 주소 `/contract/list` 는 **현재 창구**의 목록을 가리킨다.
                // 메뉴(menus.link)·구버전 앱 착지·contracts-api 서명 복귀가 이 주소를 쓴다.
                // 창구를 v6 로 옮길 땐 이 alias 를 새 트리의 목록 라우트로 옮기기만 한다.
                path: 'list',
                alias: CONTRACT_LIST_PATH,
                name: 'ContractListV5',
                component: ContractListV5,
            },
            {
                // [창구 교체 지점 2/2] 고정 주소 `/contract/types`. 모든 작성 화면의 「취소」가
                // 이곳으로 돌아온다 — v2·v3 트리 화면도 마찬가지라, 구 창구로 새지 않는다.
                path: 'types',
                alias: CONTRACT_TYPES_PATH,
                name: 'ContractTypeListV5',
                component: ContractTypeListV5,
            },
            {
                // path 는 계약유형을 그대로 쓴다 — 개정본은 덴티움 문서 V5 / 우리 계약유형 물품매매V5 /
                // 화면 v5 로 이제 세 숫자가 하나로 맞아떨어진다(이전에는 화면이 v4 여서 숫자가 셋이라
                // 어느 번호인지 헷갈렸다). /v4 는 건너뛰었다 — 2026-09-22 결정: 화면 버전을 계약서
                // 버전(물품매매V5)에 맞춘다. 계약유형은 하나뿐이라 판수를 넣지 않아도 모호하지 않다.
                //
                // name 은 MulpumV5V5.vue 의 고정 상수다(routeMap 에서 계산하지 않는다). 2026-10-29 카드가
                // V6 로 넘어간 뒤로 이 라우트는 직접 주소로만 열린다 — V5 임시저장본을 꺼내는 유일한 길이라
                // 이름이 V6 와 겹쳐 지워지지 않게 떼어 뒀다. 되돌림(카드 → V5)은 routeMap 값만 V5 로.
                path: `mulpum-v5/:templateId`,
                name: MULPUM_V5_ROUTE_NAME,
                component: MulpumV5V5,
                props: true,
            },
            {
                // 물품매매V6(2차 개정 서식). 2026-10-29 부터 유형 카드 「덴티움 물품 매매 계약서」가
                // routeMap 매핑값(= 이 name)으로 여기로 온다. ContractItemV5 가 이 name 으로 push 하므로
                // routeMap 값과 어긋나면 카드 클릭이 조용히 죽는다.
                path: `mulpum-v6/:templateId`,
                name: MULPUM_V6_ROUTE_NAME,
                component: MulpumTypeV6,
                props: true,
            },
        ]
    },
    { // 이벤트 관리
        path: RouterEnum.EventManagement.path,
        name: RouterEnum.EventManagement.name,
        redirect: `${RouterEnum.EventManagement.path}/${RouterEnum.EventManagement.EventList.path}`,
        children: [
            {   // 이벤트 목록
                path: RouterEnum.EventManagement.EventList.path,
                name: RouterEnum.EventManagement.EventList.name,
                component: EventList,
            },
            {   // 이벤트 등록
                path: RouterEnum.EventManagement.AddEvent.path,
                name: RouterEnum.EventManagement.AddEvent.name,
                component: AddEvent,
            },
            {   // 이벤트 대시보드
                path: RouterEnum.EventManagement.EventDashboard.path,
                name: RouterEnum.EventManagement.EventDashboard.name,
                component: EventDashboard,
            },
        ]
    },
    { // 이벤트 관리 V2
        path: RouterEnum.EventManagementV2.path,
        name: RouterEnum.EventManagementV2.name,
        redirect: `${RouterEnum.EventManagementV2.path}/${RouterEnum.EventManagementV2.EventList.path}`,
        children: [
            {   // 이벤트 목록 V2
                path: RouterEnum.EventManagementV2.EventList.path,
                name: RouterEnum.EventManagementV2.EventList.name,
                component: EventListV2,
            },
            {   // 이벤트 통계
                path: RouterEnum.EventManagementV2.EventStatistics.path,
                name: RouterEnum.EventManagementV2.EventStatistics.name,
                component: EventStatistics,
            },
            {   // 일자별 통계
                path: RouterEnum.EventManagementV2.DailyStatistics.path,
                name: RouterEnum.EventManagementV2.DailyStatistics.name,
                component: DailyStatistics,
            },
            {   // 참여자 세그먼트 원시 데이터
                path: RouterEnum.EventManagementV2.AttendeeSegmentRaw.path,
                name: RouterEnum.EventManagementV2.AttendeeSegmentRaw.name,
                component: AttendeeSegmentRaw,
            },
            {   // 후기 이벤트 요약
                path: 'review-summary',
                name: 'ReviewEventSummary',
                component: ReviewEventSummary,
            },
            {   // 쿠폰 발급 목록
                path: 'coupon-issued-list',
                name: 'CouponIssuedList',
                component: () => import('@/views/EventManagementV2/CouponIssuedList.vue'),
            },
            {   // 이벤트 참여자 세그먼트 분석
                path: 'attendee-segment',
                name: 'EventAttendeeSegment',
                component: () => import('@/views/EventManagementV2/EventAttendeeSegment.vue'),
            },
            {   // 이벤트 참여자 세그먼트 원시 데이터
                path: 'attendee-segment-raw',
                name: 'EventAttendeeSegmentRaw',
                component: () => import('@/views/EventManagementV2/AttendeeSegmentRaw.vue'),
            },
            {   // 이벤트 레포트
                path: RouterEnum.EventManagementV2.EventReport.path,
                name: RouterEnum.EventManagementV2.EventReport.name,
                component: EventReport,
            },
        ]
    },
    { // 매출관리
        path: RouterEnum.SalesRecordManagement.path,
        name: RouterEnum.SalesRecordManagement.name,
        redirect: process.env.DENTIUM_OBJECT_ID === companyId ?
            `${RouterEnum.SalesRecordManagement.path}/${RouterEnum.SalesRecordManagement.DentiumErpSalesRecordList.path}` :
            `${RouterEnum.SalesRecordManagement.path}/${RouterEnum.SalesRecordManagement.DentiumErpSalesRecordList.path}`,
        children: [
            {
                path: RouterEnum.SalesRecordManagement.DentiumErpSalesRecordList.path,
                name: RouterEnum.SalesRecordManagement.DentiumErpSalesRecordList.name,
                component: SalesRecordList
            },
            {
                path: RouterEnum.SalesRecordManagement.DentiumErpGoal.path,
                name: RouterEnum.SalesRecordManagement.DentiumErpGoal.name,
                component: DentiumErpGoalManagement
            },
            {
                path: RouterEnum.SalesRecordManagement.SubscriptionList.path,
                name: RouterEnum.SalesRecordManagement.SubscriptionList.name,
                component: SubscriptionList
            },
            {
                path: 'subscription/centerflow/list',
                name: 'CenterflowSubscriptionList',
                component: CenterflowSubscriptionList
            }
        ]
    },
    { // 직원 계정 조회
        path: RouterEnum.ManufacturerAccounts.path,
        name: RouterEnum.ManufacturerAccounts.name,
        redirect: `${RouterEnum.ManufacturerAccounts.path}/${RouterEnum.ManufacturerAccounts.ManufacturerAccountsList.path}`,
        children: [
            {
                path: RouterEnum.ManufacturerAccounts.ManufacturerAccountsList.path,
                name: RouterEnum.ManufacturerAccounts.ManufacturerAccountsList.name,
                component: ManuAccManage,
            }
        ]
    },
    { // 제품 QR코드 관리
        path: RouterEnum.ProductQrManagement.path,
        name: RouterEnum.ProductQrManagement.name,
        component: ProductQrPage,
    },
    { // 제품(기기) 관리 — 사이드바 미노출, 주소로만 진입 (참고용)
        path: RouterEnum.ProductDeviceManagement.path,
        name: RouterEnum.ProductDeviceManagement.name,
        component: ProductDevicePage,
    },
    { // 코드 관리 — 사이드바 노출은 메뉴 데이터(DB)로 결정, 경로 상수는 codeManagementRoutes.js
        path: CODE_MANAGEMENT.path,
        name: CODE_MANAGEMENT.name,
        redirect: `${CODE_MANAGEMENT.path}/${HANDPIECE_BARCODE.path}`,
        children: [
            {
                path: HANDPIECE_BARCODE.path,
                name: HANDPIECE_BARCODE.name,
                component: HandpieceBarcodePage,
            },
            {
                path: GENUINE_TIP_CODE.path,
                name: GENUINE_TIP_CODE.name,
                component: GenuineTipCodePage,
            },
        ],
    },
    { // 정품 라벨 등록 — 영업사원 현장 등록 (모바일 기준)
        path: RouterEnum.ProductLabel.path,
        name: RouterEnum.ProductLabel.name,
        redirect: `${RouterEnum.ProductLabel.path}/${RouterEnum.ProductLabel.List.path}`,
        /*
          hideHomeBar — 폰 상단 로고 바(App.vue 의 .mobile-home-bar)를 이 화면에서 끈다.

          **자기 상단바가 있는 화면에만 단다.** 라벨 흐름은 MobileShell 이 제목과 뒤로가기를
          담은 앱바를 스스로 그려서, 로고 바까지 얹으면 상단이 두 줄이 된다.
          앱바가 없는 화면에 달면 위가 비고, 로고를 눌러 대시보드로 가는 길도 사라진다.

          부모에 달면 자식 화면이 전부 물려받는다(자식 값이 우선). 새 라벨 화면도 자동으로 따른다.
          폰 브라우저와 신버전 앱에만 적용된다 — 구버전 앱은 네이티브 바가 로고를 그려 무관하다.
        */
        meta: { hideHomeBar: true },
        children: [
            {
                /*
                  목록만 로고 바를 둔다 — 여기는 앱바가 없고 「라벨 등록」 제목이
                  본문 안에 있어(.mk-ptitle) 로고 바가 상단을 맡는다.
                */
                path: RouterEnum.ProductLabel.List.path,
                name: RouterEnum.ProductLabel.List.name,
                component: ProductLabelList,
                meta: { hideHomeBar: false },
            },
            {
                path: RouterEnum.ProductLabel.Register.path,
                name: RouterEnum.ProductLabel.Register.name,
                component: ProductLabelRegister,
            },
            {
                // 'pending' 도 ':deviceId' 보다 앞이어야 한다 — 뒤에 두면 식별자로 잡힌다.
                path: RouterEnum.ProductLabel.Pending.path,
                name: RouterEnum.ProductLabel.Pending.name,
                component: ProductLabelPending,
            },
            {
                /*
                  ':deviceId/history' 를 ':deviceId' 보다 먼저 둔다.
                  Nest 와 달리 vue-router 는 더 구체적인 경로를 알아서 고르지만,
                  선언 순서를 맞춰 두면 읽는 사람이 헷갈리지 않는다.
                */
                path: RouterEnum.ProductLabel.History.path,
                name: RouterEnum.ProductLabel.History.name,
                component: ProductLabelHistory,
            },
            {
                // ':deviceId' 는 'list'·'register'·'pending' 보다 뒤에 와야 한다. 앞에 두면 전부 식별자로 잡는다.
                path: RouterEnum.ProductLabel.Detail.path,
                name: RouterEnum.ProductLabel.Detail.name,
                component: ProductLabelDetail,
            },
            {
                path: RouterEnum.ProductLabel.Edit.path,
                name: RouterEnum.ProductLabel.Edit.name,
                component: ProductLabelEdit,
            },
            {
                path: RouterEnum.ProductLabel.QrReassign.path,
                name: RouterEnum.ProductLabel.QrReassign.name,
                component: ProductLabelQrReassign,
            },
        ],
    },
    { // APIKEY 관리
        path: RouterEnum.ApiKeyManagement.path,
        name: RouterEnum.ApiKeyManagement.name,
        component: ApiKeyList,
    },
    { // 사업자 정보 관리
        path: RouterEnum.CompanyManagement.path,
        name: RouterEnum.CompanyManagement.name,
        component: CompanyInfo,
    },
    { // 전자계약 조회권한 관리 (SPEC-CONTRACT-VIEW-GRANT-001, 덴티움 마스터 전용)
        path: '/contract-view-policy',
        name: 'ContractViewPolicy',
        component: ContractViewPolicy,
    },
    { // 공지사항
        path: RouterEnum.NoticeManagement.path,
        name: RouterEnum.NoticeManagement.name,
        redirect: `${RouterEnum.NoticeManagement.path}/${RouterEnum.NoticeManagement.NoticeList.path}`,
        children: [
            {
                path: RouterEnum.NoticeManagement.NoticeList.path,
                name: RouterEnum.NoticeManagement.NoticeList.name,
                component: NoticeList,
            },
            {
                path: RouterEnum.NoticeManagement.NoticeDetail.path,
                name: RouterEnum.NoticeManagement.NoticeDetail.name,
                component: NoticeRead,
            },
        ]
    },
    { // swift 발행
        path: RouterEnum.Swift.path,
        name: RouterEnum.Swift.name,
        // redirect: `${RouterEnum.Swift.path}/${RouterEnum.Swift.SwiftList.path}`,
        children: [
            { // 발행 내역
                path: RouterEnum.Swift.SwiftList.path,
                name: RouterEnum.Swift.SwiftList.name,
                component: SwiftList,
            },
            { // 발행 내역
                path: RouterEnum.Swift.IssueSwift.path,
                name: RouterEnum.Swift.IssueSwift.name,
                component: IssueSwift,
            },
            { // 수정
                path: RouterEnum.Swift.ModifySwift.path,
                name: RouterEnum.Swift.ModifySwift.name,
                component: ModifySwift,
            },
        ]
    },
    { // 고객 상세
        name: RouterEnum.CustomerDetails.name,
        path: RouterEnum.CustomerDetails.path,
        component: CustomerDetails,
    },
    { // 고객 상세
        name: RouterEnum.BrandSettings.name,
        path: RouterEnum.BrandSettings.path,
        component: BrandSettings,
    },
    {
        name: 'ShareLinks',
        path: '/share-links',
        component: ShareLinksPage,
    },
    {
        name: 'ShareLinkStats',
        path: '/share-links/:code/stats',
        component: ShareLinkStatsPage,
    },
    { // 증명서 작업 요청
        name: 'CertificateWorkOrder',
        path: '/certificate-work-order',
        component: CertificateWorkOrder,
    },
    { // 증명서 작업 요청 목록
        name: 'CertificateWorkOrderList',
        path: '/certificate-work-order-list',
        component: CertificateWorkOrderList,
    },
    { // 제품 인증서 연동
        name: RouterEnum.ProductCertificateLink.name,
        path: RouterEnum.ProductCertificateLink.path,
        component: ProductCertificateLink,
    },
    { // GTIN 관리
        path: RouterEnum.GtinManagement.path,
        name: RouterEnum.GtinManagement.name,
        redirect: `${RouterEnum.GtinManagement.path}/${RouterEnum.GtinManagement.List.path}`,
        children: [
            {
                path: RouterEnum.GtinManagement.List.path,
                name: RouterEnum.GtinManagement.List.name,
                component: GtinManagementList,
            },
        ],
    },
    { // 브라이트몰 주문 조회
        path: RouterEnum.BrightmallManagement.path,
        name: RouterEnum.BrightmallManagement.name,
        redirect: `${RouterEnum.BrightmallManagement.path}/${RouterEnum.BrightmallManagement.Orders.path}`,
        children: [
            {
                path: RouterEnum.BrightmallManagement.Orders.path,
                name: RouterEnum.BrightmallManagement.Orders.name,
                component: BrightmallOrders,
            },
        ],
    },
    {
        path: RouterEnum.Promotions.path,
        name: RouterEnum.Promotions.name,
        children: [
            {
                path: RouterEnum.Promotions.List.path,
                name: RouterEnum.Promotions.List.name,
                component: PromotionList,
            },
            {
                path: RouterEnum.Promotions.Create.path,
                name: RouterEnum.Promotions.Create.name,
                component: PromotionCreate,
            },
            {
                path: RouterEnum.Promotions.Edit.path,
                name: RouterEnum.Promotions.Edit.name,
                component: PromotionCreate,
                props: true,
            },
            {
                path: RouterEnum.Promotions.Detail.path,
                name: RouterEnum.Promotions.Detail.name,
                component: PromotionDetail,
                props: true,
            },
            {
                path: RouterEnum.Promotions.Entries.path,
                name: RouterEnum.Promotions.Entries.name,
                component: PromotionEntries,
                props: true,
            },
            {
                path: RouterEnum.Promotions.EntryDetail.path,
                name: RouterEnum.Promotions.EntryDetail.name,
                component: PromotionEntryDetail,
                props: true,
            },
        ],
    },
    { // 알림톡
        path: RouterEnum.Alimtalk.path,
        name: RouterEnum.Alimtalk.name,
        redirect: `${RouterEnum.Alimtalk.path}/${RouterEnum.Alimtalk.ChannelSettings.path}`,
        children: [
            {
                path: RouterEnum.Alimtalk.ChannelSettings.path,
                name: RouterEnum.Alimtalk.ChannelSettings.name,
                component: AlimtalkChannelSettings,
            },
            {
                path: RouterEnum.Alimtalk.TemplateList.path,
                name: RouterEnum.Alimtalk.TemplateList.name,
                component: AlimtalkTemplateList,
                meta: { requiresPrimaryChannel: true },
            },
            {
                path: RouterEnum.Alimtalk.TemplateCreate.path,
                name: RouterEnum.Alimtalk.TemplateCreate.name,
                component: AlimtalkTemplateCreate,
                meta: { requiresPrimaryChannel: true },
            },
            {
                path: RouterEnum.Alimtalk.TemplateEdit.path,
                name: RouterEnum.Alimtalk.TemplateEdit.name,
                component: AlimtalkTemplateEdit,
                props: true,
                meta: { requiresPrimaryChannel: true },
            },
            {
                path: RouterEnum.Alimtalk.TemplateBulkCreate.path,
                name: RouterEnum.Alimtalk.TemplateBulkCreate.name,
                component: AlimtalkTemplateBulkCreate,
                meta: { requiresPrimaryChannel: true },
            },
        ],
    },
    {   // 모바일에서 막은 갈래로 들어왔을 때 보여 주는 안내
        path: RouterEnum.MobileBlocked.path,
        name: RouterEnum.MobileBlocked.name,
        component: MobileBlocked,
    },
    {   // 폰 하단 탭 맨 오른쪽. 계정 정보와 로그아웃이 여기 있다
        path: RouterEnum.MyPage.path,
        name: RouterEnum.MyPage.name,
        component: MyPage,
    },
    {   // 이용 내역(제조사 최소 목록, W6 C6) — DB 메뉴 대신 직원 계정 조회 화면의 버튼으로 진입
        path: '/audit-logs',
        name: 'AuditLogList',
        component: AuditLogList,
    },
    {   // 앱 사이 이동 받기(#c=<코드>&to=<경로>, K-17) — 로그인 검사 밖
        path: ENTER_PATH,
        name: 'HandoffEnter',
        component: HandoffEnter,
    },
    {   // 지원 접속 진입(#c=<코드> 교환)·끝 화면 — 로그인 검사 밖(계약 SUPPORT 9)
        path: SUPPORT_ENTER_PATH,
        name: 'SupportEnter',
        component: SupportEnter,
    },
    {
        path: SUPPORT_ENDED_PATH,
        name: 'SupportEnded',
        component: SupportEnded,
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: '/dashboard'
    },
];

const router = createRouter({
    history: process.env.VUE_APP_IS_UNICLOUD === 'true'? createWebHashHistory(process.env.BASE_URL) : createWebHistory(process.env.BASE_URL),
    routes,
    linkActiveClass: 'active',
});

// router.beforeEach((to, from, next) => {
//     if (!localStorage.getItem('accessTokenManu') &&
//         to.path !== '/signin' &&
//         to.path !== '/signup' &&
//         !to.path.includes('mobile') &&
//         !to.path.includes('notices')
//     ) {
//         next('/signin'); // 로그인 안된 유저가 회원가입, 로그인이 아닌 다른 페이지로 이동 시도 시 로그인 페이지로 보내고 모바일노티쓰는 토큰 필요 없음
//     } else if (localStorage.getItem('accessTokenManu') && (to.path == '/signin' || to.path == '/signup')) {
//         next('/dashboard'); // 로그인 된 유저가 둘 중 하나의 경로로 접근 시도 시 그냥 대시보드로 보냄
//     } else if (localStorage.getItem('accessTokenManu') && to.matched.length === 0) {
//         next('/dashboard');
//     } else {
//         next();
//     }
// });





/*

모바일로 접속한 유저에 대해 전자계약으로 리디렉션

*/
// router.beforeEach(async (to, from, next) => {
//     const accessToken = localStorage.getItem('accessTokenManu');
//     const companyName = JSON.parse(localStorage.getItem('CompanyInfo'));
//     let isZeiss = false;
//     if(companyName) {
//         isZeiss = companyName['name'].includes('자이스');
//     }
    
//     if (accessToken) {
//         if (isMobile) {
//             if (!to.path.includes('contract')) {
//                 next('/contract/list'); 
//             } else {
//                 next();
//             }
//         } else {
//             if (to.path === '/signin' || to.path === '/signup') {
//                 next('/dashboard');
//             } else {
//                 next();
//             }
//         }
//     } else {
//         if (to.path !== '/signin') {
//             next('/signin');
//         } else {
//             next();
//         }
//     }

// });
/*

자이스 제외한 유저에 대해 모바일 접속 시 전자계약으로 리디렉션

*/
// router.beforeEach(async (to, from, next) => {
//     const accessToken = localStorage.getItem('accessTokenManu');
// const companyName = JSON.parse(localStorage.getItem('CompanyInfo'));
        // let isZeiss = false;
        // if(companyName) {
        //     isZeiss = companyName['name'].includes('자이스');
        // }
    
//     if (accessToken) {
//         if (isMobile && !isZeiss) { 
//             if (!to.path.includes('contract')) {
//                 next('/contract/list');
//             } else {
//                 next(); 
//             }
//         } else {
           
//             if (to.path === '/signin' || to.path === '/signup') {
//                 next('/dashboard');
//             } else {
//                 next(); 
//             }
//         }
//     } else {
//         if (to.path !== '/signin') {
//             next('/signin'); 
//         } else {
//             next(); 
//         }
//     }
//   });

// 누구나 보는 화면(가드 맨 앞에서 늘 통과) — 첫 화면도 막혔을 때 가는 곳
const NOTICE_LIST_PATH = `${RouterEnum.NoticeManagement.path}/${RouterEnum.NoticeManagement.NoticeList.path}`;

/*
 * 권한 없는 메뉴 주소 → 그 앱 첫 화면 + 안내 한 번(11-01, studio 와 같은 방식). 막았으면 next 를 부르고 true.
 * 판정은 사이드바·하단 탭과 같은 함수(permUi.routeDenial): DB 메뉴(privileges)에 그 갈래가 있음 + enforce 면 새 권한.
 * 메뉴·권한을 아직 안 읽었으면 기다리고, 읽다 실패했으면 막지 않음(store privilegesTried·introspectTried).
 * 첫 화면: PC 대시보드·폰 경유 화면·구버전 앱 전자계약('/' redirect 그대로)·공제조합 /swift/list. 그것도 막혔거나
 * 경유 화면(첫 탭)에서 막혀 왔으면 공지 목록 — 되돌아가기 반복 없음.
 */
const SWIFT_LIST_PATH = `${RouterEnum.Swift.path}/${RouterEnum.Swift.SwiftList.path}`;

/** 로그인한 회사·계정(CompanyInfo — 지원 탭이면 그 탭 값) */
function signedInCompany() {
    try {
        return JSON.parse(getProfile('CompanyInfo'));
    } catch (e) {
        return null;
    }
}
const isSeolbiCompany = (company) => !!company?.name?.includes('기계설비건설공제조합');
const userIdOf = (company) => company?.accountInfo?.username || store.state.userInfo.userId;

/**
 * 지금 받아 둔 메뉴·권한으로 이 주소를 막나(permUi.routeDenial — 사이드바와 같은 판정). 가드와 화면 안 버튼이 같이 씀.
 * 메뉴를 아직 못 받았거나 실패했으면 막지 않음(관리자·권한 있는 직원의 버튼이 깜빡 사라지지 않게).
 * 공제조합의 대시보드는 가드가 /swift/list 로 바꿔 보내므로 그 화면으로 판정
 */
export function routeDenialNow(path) {
    // 프로모션 운영(11-02): 지원 탭·운영자 관리자 본인 로그인(11-03)이면 회사 메뉴와 상관없이 열고, 그 밖 일반 로그인은 늘 막음(안내는 MENU_DENIED_TEXT)
    if (isSupportOnlyPath(path)) return isSupportTab() || isOperatorAdminToken(getAccessToken()) ? null : { codes: null };
    const company = signedInCompany();
    const userId = userIdOf(company);
    const tried = store.state.privilegesTried;
    const categories = userId && tried.userId === userId && tried.ok ? store.state.privileges?.category : null;
    const intro = store.state.introspect;
    const target = isSeolbiCompany(company) && String(path).includes('dashboard') ? SWIFT_LIST_PATH : path;
    return routeDenial({ path: target, categories, perms: intro?.profile?.perms, modes: intro?.modes });
}

/** 화면 안 배너·버튼·링크: 갈 수 없는 화면이면 숨김(누르면 「권한 없음」으로 튕기는 막다른 길 방지) */
export function canOpen(path) {
    return !routeDenialNow(path);
}

async function denyHiddenMenu(to, from, next) {
    if (!menuKeyOfLink(to.path)) return false;
    const company = signedInCompany();
    const isSeolbi = isSeolbiCompany(company);
    // 공제조합의 대시보드는 아래 갈래가 /swift/list 로 바꿔 보냄 — 판정은 그 화면에서
    if (isSeolbi && to.path.includes('dashboard')) return false;
    const userId = userIdOf(company);
    // 읽기 실패는 삼킴 — main·development 의 store 는 메뉴 읽기 실패를 다시 던짐. 실패면 아래 판정이 막지 않음
    await Promise.all([
        userId && store.state.privilegesTried.userId !== userId ? store.dispatch('ensurePrivileges', userId).catch(() => null) : null,
        store.state.introspectTried ? null : store.dispatch('loadIntrospect').catch(() => null),
    ]);
    const judge = routeDenialNow;
    const denial = judge(to.path);
    if (!denial) return false;
    // 새 권한 코드면 표준 문구 「권한이 없습니다(필요: 한국어 이름)」, 이름표가 없거나 옛 규칙이면 영문 코드 없이 일반 안내
    const labelOf = (c) => store.state.permLabels[c];
    if (denial.codes && !denial.codes.every(labelOf)) await store.dispatch('loadPermLabels');
    const named = !!denial.codes && denial.codes.every(labelOf);
    message.warning({
        content: named ? permissionDeniedText({ response: { data: { required: denial.codes } } }, labelOf) : MENU_DENIED_TEXT,
        key: 'ROUTE_PERMISSION_DENIED',
    });
    const first = isSeolbi ? SWIFT_LIST_PATH : routes.find((r) => r.path === '/').redirect();
    next(first === to.path || from.path === RouterEnum.MobileLanding.path || judge(first) ? NOTICE_LIST_PATH : first);
    return true;
}

/**
 * 지원 탭도 대표와 같은 회사별 규칙(11-01 지원 = 그 회사 최상위 관리자와 같은 화면) — 아래 일반 갈래(localStorage 토큰)의
 * 알림톡 대표 채널 필수·기계설비건설공제조합 첫 화면·폰 막기를 지원 탭 값(CompanyInfo = 그 탭 sessionStorage)으로 같은 판정.
 * 일반 갈래를 이 함수로 바꾸지 않은 것은 이 파일 보존 검사가 분기점 대비 삭제 0줄을 요구해서(줄 추가만).
 * 알림톡 메뉴 유무는 앞의 denyHiddenMenu 가 같은 안내로 막음. 처리했으면 true(next 를 부름), 아니면 false
 */
async function supportCompanyRule(to, next) {
    if (to.path === '/notices/list') return false;
    if (to.path.startsWith('/alimtalk') && to.meta?.requiresPrimaryChannel) {
        let channel;
        try {
            channel = await store.dispatch('fetchPrimaryChannel');
        } catch (error) {
            console.error('Failed to check primary channel:', error);
            next({ name: RouterEnum.Alimtalk.ChannelSettings.name });
            return true;
        }
        if (!channel) {
            alert('알림톡 발송 채널을 먼저 등록해주세요. 채널 설정 페이지로 이동합니다.');
            next({ name: RouterEnum.Alimtalk.ChannelSettings.name });
            return true;
        }
    }
    const company = signedInCompany();
    if (isSeolbiCompany(company)) {
        if (!to.path.includes('dashboard')) return false;
        next(SWIFT_LIST_PATH);
        return true;
    }
    if (isMobile() && !company?.name?.includes('자이스') && to.name !== RouterEnum.MobileBlocked.name && isBlockedOnMobile(to.path)) {
        next({ name: RouterEnum.MobileBlocked.name, query: { from: to.path } });
        return true;
    }
    return false;
}

/*

자이스 제외한 유저에 대해 모바일 접속 시 전자계약 또는 공지사항으로 리디렉션

*/
router.beforeEach(async (to, from, next) => {
    // 로그인 검사 밖 화면(지원 진입·끝, 앱 사이 이동 받기). 이 가드는 줄 추가만 함 — 보존 검사가 분기점 대비 삭제 0줄을 요구
    const openPaths = [SUPPORT_ENTER_PATH, SUPPORT_ENDED_PATH, ENTER_PATH];
    if (openPaths.includes(to.path)) {
        next();
        return;
    }
    // 옛 토큰 쿼리 받기는 지원 탭이어도 아래에서 이어받음(일반 로그인으로 바뀜 — clearSupport)
    const legacyTokenEntry = typeof to.query.token === 'string' && !!to.query.token;
    // 지원이 끝난 탭(래치): 위 세 화면 밖은 끝 화면으로(일반 로그인 판정으로 넘어가지 않음)
    if (isSupportEnded() && !legacyTokenEntry) {
        next(supportEndedUrl(getSupportSession()?.reason));
        return;
    }
    // 지원 탭: 그 탭 sessionStorage 토큰으로만 판정하고, 아래 일반 로그인 판정(localStorage)은 지나지 않음
    if (isSupportTab() && !legacyTokenEntry) {
        if (!getAccessToken()) next(SUPPORT_ENDED_PATH);
        else if (to.path === '/signin' || to.path === '/signup') next('/dashboard');
        // 지원 탭도 같은 규칙(권한 없는 메뉴 주소 → 첫 화면 + 안내)
        else if (await denyHiddenMenu(to, from, next)) return;
        // 대표와 같은 회사별 규칙(설비공제 첫 화면·알림톡 대표 채널·폰 막기)
        else if (await supportCompanyRule(to, next)) return;
        else next();
        return;
    }
    // 토큰 기반 자동 로그인 (리셀러 대리 로그인)
    const urlToken = to.query.token;
    if (urlToken && typeof urlToken === 'string') {
        // 기존 세션 정보 모두 초기화 (다른 제조사 계정으로 전환을 위해)
        clearSupport(); // 이 탭이 지원 탭(끝난 래치 포함)이었으면 일반 탭으로 — 일반 로그인이 이어받음
        localStorage.removeItem('accessTokenManu');
        localStorage.removeItem('refreshTokenManu');
        localStorage.removeItem('mfaTokenManu'); // 증표는 URL 로 넘어오지 않음 → 첫 쓰기에서 step-up
        localStorage.removeItem('nameManu');
        localStorage.removeItem('managerManu');
        localStorage.removeItem('companyManu');
        localStorage.removeItem('companyImageUrlManu');
        localStorage.removeItem('CompanyInfo');
        localStorage.removeItem('manufacturerNameManu');
        localStorage.removeItem('locale');

        // 새 토큰을 localStorage에 저장
        localStorage.setItem('accessTokenManu', urlToken);

        // 페이지 새로고침으로 Vuex store와 컴포넌트 상태 완전 초기화
        // (SPA 특성상 Vue Router next()로는 컴포넌트가 다시 마운트되지 않음)
        window.location.href = RouterEnum.Dashboard.path;
        return;
    }

    const accessToken = localStorage.getItem('accessTokenManu');
    const companyName = JSON.parse(localStorage.getItem('CompanyInfo'));
    let isZeiss = false;
    if(companyName) {
        isZeiss = companyName['name'].includes('자이스');
    }
    let isSeolbi = false;
    if(companyName) {
        isSeolbi = companyName['name'].includes('기계설비건설공제조합');
    }
    // !to.path.includes('contract'
    if (accessToken) {
        if (to.path === '/notices/list') {
            next();
            return;
        }
        // 권한 없는 메뉴 주소(사이드바에 안 보이는 갈래) → 첫 화면 + 안내. 알림톡도 여기서 같은 안내로 막힘
        if (await denyHiddenMenu(to, from, next)) return;

        // 알림톡 접근 제어: privileges에 알림톡 메뉴가 없으면 차단
        if (to.path.startsWith('/alimtalk')) {
            const privileges = store.state.privileges;
            // privileges.category가 존재하면(로드 완료 상태) 메뉴 체크
            // undefined면(아직 미로드) 통과 허용 - Sidenav가 로드 후 이후 네비게이션에서 체크됨
            if (Array.isArray(privileges?.category)) {
                const hasAlimtalkMenu = privileges.category.some(cat =>
                    cat.menu?.some(menu => menu.link?.startsWith('/alimtalk'))
                );
                if (!hasAlimtalkMenu) {
                    // 위 denyHiddenMenu 와 같은 안내(같은 key 라 겹쳐 뜨지 않음). 대시보드가 막혔으면 그 이동에서 공지로
                    message.warning({ content: MENU_DENIED_TEXT, key: 'ROUTE_PERMISSION_DENIED' });
                    next('/dashboard');
                    return;
                }
            }

            // 대표 채널 필수 라우트 체크
            if (to.meta?.requiresPrimaryChannel) {
                try {
                    const channel = await store.dispatch('fetchPrimaryChannel');
                    if (!channel) {
                        alert('알림톡 발송 채널을 먼저 등록해주세요. 채널 설정 페이지로 이동합니다.');
                        next({ name: RouterEnum.Alimtalk.ChannelSettings.name });
                        return;
                    }
                } catch (error) {
                    console.error('Failed to check primary channel:', error);
                    next({ name: RouterEnum.Alimtalk.ChannelSettings.name });
                    return;
                }
            }
        }

        if(isSeolbi){
            if (to.path.includes('dashboard')) {
                next('/swift/list'); 
            } else {
                next();
            }
        }else if (isMobile() && !isZeiss) {
            /*
             * 모바일 접근 제어.
             *
             * 예전에는 화이트리스트였다 — 전자계약·브라이트몰 말고는 전부 /contract/list 로 돌려보냈다.
             * 그래서 권한이 있어도 폰에서는 다른 화면에 들어갈 수 없었다.
             *
             * 이제는 막을 갈래만 막는다(utils/mobileAccess.js). 목록은 사이드바 메뉴 필터와 공유하므로
             * 메뉴에 보이는데 누르면 튕기는 일이 생기지 않는다.
             * 안내 화면 자체는 당연히 막히면 안 되므로 먼저 통과시킨다.
             */
            if (to.name === RouterEnum.MobileBlocked.name) {
                next();
            } else if (isBlockedOnMobile(to.path)) {
                next({ name: RouterEnum.MobileBlocked.name, query: { from: to.path } });
            } else if (to.path === '/signin' || to.path === '/signup') {
                /*
                 * 이미 로그인한 사람이 로그인 화면에 닿았다. 대시보드가 아니라 첫 탭으로 보낸다.
                 *
                 * 폰에서 대시보드는 탭에 없을 수 있는 화면이다. 로그인 직후 경로는
                 * replace 로 바꿔 히스토리에 /signin 이 남지 않게 했지만(signInMixin),
                 * 다른 경로로 여기 닿는 경우를 위한 안전망이다.
                 *
                 * 첫 탭 계산은 경유 화면에 맡긴다 — 여기서 store 를 기다리면
                 * 화면 이동 전체가 그만큼 늦어진다.
                 *
                 * 구버전 앱은 전자계약 목록으로 — 위 '/' 리다이렉트와 같은 이유다.
                 * 001 이전에는 이 자리도 화이트리스트에 걸려 /contract/list 로 갔다.
                 */
                next(isLegacyHostApp()
                    ? LEGACY_APP_LANDING
                    : RouterEnum.MobileLanding.path);
            } else {
                next();
            }
        } else {
            if (to.path === '/signin' || to.path === '/signup') {
                next('/dashboard');
            } else {
                next();
            }
        }
    } else {
        if (to.path !== '/signin') {
            if (to.path === '/reset-pwd') {
                next();
            } else {
                next('/signin');
            }
        } else {
            next();
        }
    }
});

// 이용 내역의 화면 위치(X-Audit-Screen) — 이동이 끝난 라우트로 바꿔 둠
router.afterEach(setAuditScreen);

export default router;
